// The app's toasts (aithlo-frontend#34, #35): one-way notices — "Saved", or
// a failure the athlete would otherwise never see — instead of a native
// alert() that freezes the tab. A tiny store, so any module can raise one
// and <Toaster /> (components/ui/toast.jsx, mounted once in App.jsx) shows
// them. Pure apart from the timers, so it is tested without a browser.
//
// Failures follow the rule in v2/failures.js: a warm headline that blames
// nobody, the backend's own reason only under Details (for support).

import { backendReason, OFFLINE_TEXT } from '../v2/failures';

const DURATION = { success: 4000, info: 5000, error: 8000 };
let nextId = 1;
let items = [];
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn(items));

/** Subscribe to the list of toasts on screen; returns the unsubscribe. */
export function subscribe(fn) {
  listeners.add(fn);
  fn(items);
  return () => listeners.delete(fn);
}

export function dismiss(id) {
  const before = items.length;
  items = items.filter((t) => t.id !== id);
  if (items.length !== before) emit();
}

/**
 * Show a toast. `message` is a string, or `{text, details}` (what
 * failures.js `formFailure` returns). Returns its id.
 */
export function toast(message, { variant = 'info', details = '', duration } = {}) {
  const text = typeof message === 'string' ? message : message?.text;
  if (!text) return null;
  const more = (typeof message === 'object' && message?.details) || details || '';
  // The same words already on screen are the same toast (a double tap).
  const same = items.find((t) => t.text === text && t.variant === variant);
  if (same) return same.id;
  const id = nextId++;
  items = [...items.slice(-3), { id, text, details: more, variant }];
  emit();
  const ms = duration ?? DURATION[variant] ?? DURATION.info;
  if (ms > 0 && typeof setTimeout === 'function') setTimeout(() => dismiss(id), ms);
  return id;
}

toast.success = (message, opts) => toast(message, { ...opts, variant: 'success' });
toast.info = (message, opts) => toast(message, { ...opts, variant: 'info' });
toast.error = (message, opts) => toast(message, { ...opts, variant: 'error' });

/** The small print for an axios error: the backend's reason and the status. */
export function errorDetails(err) {
  const status = err?.response?.status;
  const why = backendReason(err?.response?.data) || (!err?.response && err?.message) || '';
  return [why || null, status ? `(${status})` : null].filter(Boolean).join(' ');
}

/** No answer at all from the server (offline, or the request never landed). */
export const isNetworkError = (err) => Boolean(err) && !err.response
  && (err.code === 'ERR_NETWORK' || err.message === 'Network Error' || err.network === true);

/**
 * A failed user action, made visible: log it (once, for us) and toast the
 * athlete's words, with the backend's reason under Details. `text` is the
 * headline ("Couldn't save your bio — try again."); offline says so instead.
 */
export function toastFailure(text, err, { log = text } = {}) {
  console.error(log, err);
  if (isNetworkError(err)) return toast.error(OFFLINE_TEXT);
  return toast.error(text, { details: errorDetails(err) });
}

/**
 * For screens where the backend's reason IS the message (admin tools, a
 * validation answer like "username taken"): show it as the headline, with
 * `fallback` when there is none.
 */
export function toastDetail(err, fallback) {
  console.error(fallback, err);
  if (isNetworkError(err)) return toast.error(OFFLINE_TEXT);
  return toast.error(backendReason(err?.response?.data) || fallback);
}

/** Tests only: clear the store. */
export function _reset() {
  items = [];
  emit();
}
